/**
 * Widget Catalogue Input - Isolated Version
 *
 * A fork of CatalogueInput that uses only eg-* prefixed components
 * without any Bootstrap or react-router dependencies.
 */

import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import _ from "lodash";
import { components } from "react-select";
import { catalogByPart } from "../../apis";

// Isolated components
import { EgCol, EgButton, EgModal } from './WidgetGrid';
import { EgSelect, EgLabel, EgRadio, EgCheckbox } from './WidgetInputs';

// Form utilities
import { hdlSchm, hdlChg, hdlConstraints } from '../WidgetPreviewForm';
import {
  INPUT_TYPE_CATALOGUE_RADIO,
  INPUT_TYPE_CATALOGUE_RADIO_CONDITIONAL,
  INPUT_TYPE_CATALOGUE_RADIO_DESCRIPTION,
  INPUT_TYPE_CATALOGUE_RADIO_DESCRIPTION_CONDITIONAL,
  INPUT_TYPE_CATALOGUE_SELECT,
  INPUT_TYPE_CATALOGUE_SELECT_CONDITIONAL,
  especialCatalogues,
  isEspecialCatalogue,
  isForLanding
} from "../../components/Form/consts";
import I18nText from "../../components/Form/PreviewForm/i18n";

const checkIfIsAutoSelection = (schema, scope, nestNum) => {
  if (!Array.isArray(schema.hiddenLvls)) return false;
  if (!schema.hiddenLvls.includes(nestNum)) return false;
  if (scope.children.length === 1) return true;
  return false;
};

const getConstraintValues = (constraints, schema) => {
  const filtredConstraints = constraints.filter(
    (item) => item.catKey == schema.key && item.selected === true
  );
  const valuesConstraints = filtredConstraints
    .map((item) => item.constraints)
    .flat(Infinity);
  return valuesConstraints;
};

// Error messages component
const ReturnErrorMessages = ({ tryNext, valid }) => {
  return (
    <>
      {tryNext &&
        Array.isArray(valid) &&
        valid?.length > 0 &&
        valid.map((err, idx) => (
          <div key={idx} className="eg-invalid-msg">
            {err}
          </div>
        ))}
    </>
  );
};

// Modal for examples
const ModalExamples = ({ itemExamples, setShowExampleModal, theme }) => {
  const lang = localStorage.getItem("eg_lang") || "es";
  const i18n = {
    "catalogues_these_are_examples": I18nText({ lang, translation: "catalogues_these_are_examples" }),
  };

  return (
    <EgModal
      show={true}
      onHide={() => setShowExampleModal(false)}
      title={i18n["catalogues_these_are_examples"]}
    >
      <div className="eg-modal-examples">
        {itemExamples.map((itemTxt, idx) => (
          <p key={idx} className="eg-example-item">{itemTxt}</p>
        ))}
      </div>
    </EgModal>
  );
};

// Radio input component for catalogues
const CatalogueRadioInput = ({
  scope,
  pathData,
  type,
  nestNum,
  returnValidClass,
  showErrorLastChild,
  GetDescritionAndExamples,
  handleKeyChange,
  selectedKey,
  isSelected,
  orderChildren,
  theme,
}) => {
  const isDark = theme === 'dark';

  if (
    type === INPUT_TYPE_CATALOGUE_RADIO ||
    type === INPUT_TYPE_CATALOGUE_RADIO_CONDITIONAL
  ) {
    return (
      <div className="eg-container-checkbox2">
        {scope.children &&
          orderChildren(scope.children).map((item, idxx) => {
            if (item.is_active) {
              return (
                <div
                  role="button"
                  className={`eg-radio-option ${isDark ? 'eg-dark' : ''} ${isSelected(selectedKey === item.key)}`}
                  key={idxx}
                  onClick={(e) =>
                    selectedKey !== item.key
                      ? handleKeyChange(item.key, "click")
                      : () => {}
                  }
                >
                  <div
                    key={item.key}
                    className="eg-form-check"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      className={`eg-form-check-input ${showErrorLastChild ? returnValidClass() : ""}`}
                      id={`${pathData}-${nestNum}-${idxx}`}
                      type="radio"
                      value={item.key}
                      name={item.path}
                      checked={selectedKey == item.key}
                      onChange={(e) => handleKeyChange(e.target.value, "change")}
                    />
                    <label
                      role="button"
                      className={`eg-form-check-label ${isDark ? 'eg-dark' : ''} ${isSelected(selectedKey === item.key)}`}
                      htmlFor={`${pathData}-${nestNum}-${idxx}`}
                    >
                      {item.label}
                    </label>
                  </div>
                </div>
              );
            }
            return null;
          })}
      </div>
    );
  }

  // Radio with description
  return (
    <>
      {scope.children &&
        orderChildren(scope.children).map((item, idxx) => {
          if (item.is_active) {
            return (
              <div
                role="button"
                className={`eg-container-checkbox ${isDark ? 'eg-dark' : ''} ${isSelected(selectedKey === item.key)}`}
                key={idxx}
                onClick={(e) =>
                  selectedKey !== item.key
                    ? handleKeyChange(item.key)
                    : () => {}
                }
              >
                <div
                  key={item.key}
                  className="eg-form-check"
                  onClick={(e) => e.stopPropagation()}
                >
                  <input
                    className={`eg-form-check-input ${showErrorLastChild ? returnValidClass() : ""}`}
                    id={`${pathData}-${nestNum}-${idxx}`}
                    type="radio"
                    value={item.key}
                    name={item.path}
                    checked={selectedKey == item.key}
                    onChange={(e) => handleKeyChange(e.target.value)}
                  />
                  <label
                    role="button"
                    className={`eg-form-check-label ${isDark ? 'eg-dark' : ''} ${isSelected(selectedKey === item.key)}`}
                    htmlFor={`${pathData}-${nestNum}-${idxx}`}
                  >
                    {item.label}
                  </label>
                  {(type === INPUT_TYPE_CATALOGUE_RADIO_DESCRIPTION ||
                    type === INPUT_TYPE_CATALOGUE_RADIO_DESCRIPTION_CONDITIONAL) && (
                    <GetDescritionAndExamples item={item} />
                  )}
                </div>
              </div>
            );
          }
          return null;
        })}
    </>
  );
};

// Main Catalogue Input Component
export const WidgetCatalogueInput = ({
  schema,
  scope,
  value,
  valid,
  origin,
  pathSchema,
  pathData,
  entireSchema,
  entireFormData,
  setSchemaState,
  setFormData,
  constraints = [],
  setConstraints = () => {},
  tryToNext,
  handleValidate,
  returnValidClass,
  rerenderParent,
  containerName,
  containerDefault,
  path = "",
  labelShowed = false,
  nestNum = 0,
  forEdition = false,
  lang: forcedLang,
  theme = 'light',
  formParams = {}
}) => {
  const lang = forcedLang || localStorage.getItem("eg_lang") || "es";
  const isDark = theme === 'dark';

  const i18n = {
    "catalogues_examples_title": I18nText({ lang, translation: "catalogues_examples_title" }),
    "catalogues_placeholder": I18nText({ lang, translation: "catalogues_placeholder" }),
    "catalogues_inactive": I18nText({ lang, translation: "catalogues_inactive" }),
  };

  // Path of catalogue value
  const pathTemp = value ? value : "";
  const splitedArr = pathTemp.split("::");
  const slicedArr = splitedArr.slice(0, 2 * (nestNum + 1));
  const currPath = slicedArr.join("::");

  // Constraints
  const constraintValues = getConstraintValues(constraints, schema);
  const [currentConstraints, setCurrentConstraints] = useState(null);
  const [hideInputByContraints, setHideInputByContraints] = useState(false);

  // Modals
  const [showExampleModal, setShowExampleModal] = useState(false);

  // States
  const [selectedKey, setSelectedKey] = useState(scope.selected || "");
  const [showLabel, setShowLabel] = useState(false);
  const [itemExamples, setitemExamples] = useState([]);
  const [rerenderItself, setRerenderItself] = useState(false);
  const [rerenderChild, setRerenderChild] = useState(false);

  // Catalogue handling
  const showErrorLastChild = scope.selected ? false : true;
  const isLastChild = scope.lastChild;

  // Schema keys
  const type = schema.type;
  const grid = schema.grid;
  const sensitive = schema.sensitive;
  const catalogue = schema.catalogue;
  const isOwn = schema.isOwn;
  const hidden = schema.hidden;
  const defaultSelection = schema.container_default || containerDefault || null;
  const hasCatConstraints = "otherCatConstraints" in schema;
  const hideAndAutoselection = checkIfIsAutoSelection(schema, scope, nestNum);

  // Utility objects
  const utilHdlSch = { entireSchema, setSchemaState, path, pathSchema };
  const utilHdlChg = { origin, entireFormData, setFormData };
  const utilConstraints = { origin, constraints, setConstraints };
  const params = { catalogue, sensitive, isOwn, origin };

  useEffect(() => {
    setRerenderChild(true);
  }, [selectedKey]);

  useEffect(() => {
    if (rerenderChild) setRerenderChild(false);
  }, [rerenderChild]);

  useEffect(() => {
    if (rerenderItself) setRerenderItself(false);
  }, [rerenderItself]);

  useEffect(() => {
    if (!scope.selected && hideAndAutoselection) {
      handleKeyChange(scope.children[0].key);
    }
    if (!scope.selected) {
      const childIndex = handleQuerys();
      if (childIndex >= 0) {
        handleKeyChange(scope.children[childIndex].key);
      }
    }
    if (!scope.selected && defaultSelection != null) {
      const idxChild = scope.children.findIndex(item => item.key === defaultSelection);
      if (idxChild >= 0) {
        handleKeyChange(scope.children[idxChild].key);
      }
    }
    setSelectedKey(scope.selected);
  }, [scope]);

  useEffect(() => {
    if (!_.isEqual(_.sortBy(constraintValues), _.sortBy(currentConstraints))) {
      setCurrentConstraints(constraintValues);
    }
  }, [constraints]);

  useEffect(() => {
    if (currentConstraints) {
      if (currentConstraints.includes(currPath)) {
        hdlSchm({ action: "nullSelection", ...utilHdlSch });
        handleDataNull();
        handleValidate(false);
        setRerenderItself(true);
      }
    } else {
      setCurrentConstraints(constraintValues);
    }
  }, [currentConstraints]);

  useEffect(() => {
    const childrenKeys = scope?.children?.map(item => item.path_locales);
    if (_.isEqual(_.sortBy(constraintValues), _.sortBy(childrenKeys))) {
      setHideInputByContraints(true);
      if (schema?.required) {
        hdlSchm({ action: "nullSelection", ...utilHdlSch });
        handleDataNull();
        handleValidate(true);
      }
    } else if (hideInputByContraints) {
      setHideInputByContraints(false);
      if (schema?.required) {
        handleValidate(false);
      }
    }
  }, [currentConstraints]);

  const handleKeyChange = (newKey) => {
    if (newKey == selectedKey) return;
    if (!newKey) {
      if (scope.selected !== newKey) {
        hdlSchm({ action: "nullSelection", ...utilHdlSch });
        handleDataNull();
        handleValidate(false);
      }
    } else if (scope.selected !== newKey) {
      const actualScope = scope.children.find((obj) => obj.key === newKey);
      const pathOnBack = actualScope?.path;
      if (pathOnBack) {
        catalogByPart({ is_own: isOwn, catalogue, path: pathOnBack, ...(forEdition && { show_all: true }) }, lang)
          .then((resp) => {
            if (resp?.data?.length === 0) {
              hdlSchm({ action: "noNextPath", selected: newKey, ...utilHdlSch });
              handleValidate(true);
              handleDataNormal(newKey);
            } else {
              hdlSchm({ action: "selection", children: resp.data, selected: newKey, ...utilHdlSch });
              handleValidate(false);
              handleDataEndpoint({ pathOnBack, scope: resp.data });
            }
          })
          .catch(console.log);
      } else {
        hdlSchm({ action: "noNextPath", selected: newKey, ...utilHdlSch });
        handleValidate(true);
        handleDataNormal(newKey);
      }
    }
  };

  const handleDataEndpoint = ({ pathOnBack, scope, acumulatedPath }) => {
    if (pathOnBack) {
      hdlChg({
        e: { target: { name: catalogue, value: pathOnBack } },
        entirePathData: pathData,
        params,
        ...utilHdlChg,
      });
      if (hasCatConstraints) hdlConstraints({ value: pathOnBack, ...utilConstraints });
    } else {
      hdlChg({
        e: { target: { name: catalogue, value: acumulatedPath } },
        entirePathData: pathData,
        params,
        ...utilHdlChg,
      });
      if (hasCatConstraints) hdlConstraints({ value: acumulatedPath, ...utilConstraints });
    }
  };

  const handleDataNormal = (newKey) => {
    const valueTemp = value ? value : "";
    const splitedArr = valueTemp.split("::");
    const slicedArr = splitedArr.slice(0, 2 * nestNum);
    const joined = slicedArr.join("::");
    if (joined) {
      hdlChg({
        e: { target: { name: catalogue, value: `${joined}::type::${newKey}` } },
        entirePathData: pathData,
        params,
        ...utilHdlChg,
      });
      if (hasCatConstraints) hdlConstraints({ value: `${joined}::type::${newKey}`, ...utilConstraints });
    } else {
      hdlChg({
        e: { target: { name: catalogue, value: `type::${newKey}` } },
        entirePathData: pathData,
        params,
        ...utilHdlChg,
      });
      if (hasCatConstraints) hdlConstraints({ value: `type::${newKey}`, ...utilConstraints });
    }
  };

  const handleDataNull = () => {
    const valueTemp = value ? value : "";
    const splitedArr = valueTemp.split("::");
    const slicedArr = splitedArr.slice(0, 2 * nestNum);
    const joined = slicedArr.join("::");
    if (joined) {
      hdlChg({
        e: { target: { name: catalogue, value: `${joined}` } },
        entirePathData: pathData,
        params,
        ...utilHdlChg,
      });
      if (hasCatConstraints) hdlConstraints({ value: `${joined}`, ...utilConstraints });
    } else {
      hdlChg({
        e: { target: { name: catalogue, value: `` } },
        entirePathData: pathData,
        params,
        ...utilHdlChg,
      });
      if (hasCatConstraints) hdlConstraints({ value: ``, ...utilConstraints });
    }
  };

  const handleQuerys = () => {
    const especialFound = especialCatalogues.find(item => item.catalogue == catalogue && item.isOwn == isOwn && item.nestNum == nestNum);
    const undefinedChildIndex = Array.isArray(scope.children) ? scope.children.findIndex(item => item.key === "undefined") : -1;
    if (especialFound && undefinedChildIndex >= 0) {
      return undefinedChildIndex;
    }
    if (especialFound && Array.isArray(scope.children)) {
      const idxContext = scope.children.findIndex(item => item.key === formParams[especialFound.query]);
      const byContext = idxContext < 0 ? null : idxContext;
      return byContext ?? -1;
    }
    return -1;
  };

  const orderChildren = (children) => {
    const orderedChildren = children.sort((a, b) => a.label - b.label);
    const orderOther = orderedChildren.sort((a, b) => {
      if (a.label.toLowerCase() === "other" || a.label.toLowerCase() === "otro") {
        return 1;
      } else if (b.label.toLowerCase() === "other" || b.label.toLowerCase() === "otro") {
        return -1;
      }
      return 0;
    });
    const finalOrder = orderOther.filter((child) => {
      for (const constraint of constraintValues) {
        if (child.path_locales.startsWith(constraint)) {
          return false;
        }
      }
      return true;
    });
    return finalOrder;
  };

  const getOptions = () => {
    var options = [];
    const childrens = orderChildren(scope.children);

    for (var i = 0; i < childrens.length; i++) {
      if (childrens[i].is_active === true) {
        options.push({
          value: childrens[i].key,
          name: childrens[i].path,
          label: childrens[i].locales?.lang ? childrens[i].locales.lang : childrens[i].label,
          description: childrens[i].description,
          examples: childrens[i].examples,
          container_name: childrens[i].container_name,
          locales: childrens[i].locales,
          is_active: true,
        });
      } else if (childrens[i].is_active === false && forEdition) {
        options.push({
          value: childrens[i].key,
          name: childrens[i].path,
          label: childrens[i]?.locales?.lang ? childrens[i]?.locales.lang : childrens[i].label,
          description: childrens[i].description,
          examples: childrens[i].examples,
          container_name: childrens[i].container_name,
          locales: childrens[i].locales,
          is_active: false,
        });
      }
    }
    return options;
  };

  const isSelected = (condition) => (condition ? "eg-selected" : "");

  const handleSetExamples = (examplesOfItem, event) => {
    event.stopPropagation();
    setitemExamples(examplesOfItem);
    setShowExampleModal(true);
  };

  const GetDescritionAndExamples = ({ item }) => {
    const description = item.description ? item.description : null;
    const examples = item.examples ? item.examples : null;

    return (
      <>
        {description && typeof description === "string" && (
          <p className={`eg-description ${isDark ? 'eg-dark' : ''} ${isSelected(selectedKey === item.key)}`}>
            {description}
          </p>
        )}
        {examples && Array.isArray(examples) && (
          <EgButton
            label={i18n["catalogues_examples_title"]}
            variant="primary"
            outline
            size="sm"
            onClick={(e) => handleSetExamples(examples, e)}
          />
        )}
      </>
    );
  };

  const getContainerName = () => {
    if (!selectedKey) return null;
    return scope?.children.find((item) => item.key === selectedKey)?.container_name || null;
  };

  const getContainerDefault = () => {
    if (!selectedKey) return null;
    return scope?.children.find((item) => item.key === selectedKey)?.container_default || null;
  };

  // Custom Option component for react-select
  const Option = ({ ...props }) => (
    <components.Option {...props}>
      <div className="eg-select-option">
        <div className="eg-select-option-label">
          {props.data.label}{" "}
          {props.data.is_active === false && `(${i18n["catalogues_inactive"]})`}
        </div>
        <GetDescritionAndExamples item={props.data} />
      </div>
    </components.Option>
  );

  const IconSingleValue = (props) => (
    <components.SingleValue {...props}>
      {props.data.label}{" "}
      {props.data.is_active === false && `(${i18n["catalogues_inactive"]})`}
    </components.SingleValue>
  );

  const handleHidden = () => {
    if (!isForLanding) return false;
    if (hidden && !isEspecialCatalogue(catalogue)) return true;
    const childIndex = handleQuerys();
    if (childIndex >= 0 && hidden) return true;
    if (hideAndAutoselection) return true;
    return false;
  };

  const middlewareHidden = () => {
    if (!isForLanding) {
      if (!labelShowed && showLabel === false) {
        setShowLabel(true);
      }
      return false;
    }
    const hide = handleHidden();
    if (!labelShowed && !hide && showLabel === false) {
      setShowLabel(true);
    }
    return hide;
  };

  // SELECT type catalogue
  if (
    type === INPUT_TYPE_CATALOGUE_SELECT ||
    type === INPUT_TYPE_CATALOGUE_SELECT_CONDITIONAL
  ) {
    return (
      <>
        {showExampleModal && (
          <ModalExamples
            itemExamples={itemExamples}
            setShowExampleModal={setShowExampleModal}
            theme={theme}
          />
        )}
        {!middlewareHidden() && !hideInputByContraints && (
          <EgCol md={schema.grid || 12} className="eg-preview-input-container">
            {(showLabel && !labelShowed && !containerName) && (
              <div className="eg-flex">
                <EgLabel required={schema.required}>
                  {schema.locales?.[lang] === undefined ? schema.label : schema.locales[lang]}
                </EgLabel>
                {hidden && !isForLanding && (
                  <span className="eg-icon-hidden eg-ms-2">👁</span>
                )}
              </div>
            )}
            {containerName && (
              <EgLabel required={schema.required && !labelShowed}>
                {containerName}
              </EgLabel>
            )}
            <div className="eg-preview-input-container-inp">
              {scope.children && !rerenderItself && (
                <div className={`${showErrorLastChild ? returnValidClass("container") : ""} ${forEdition ? "eg-flex" : ""}`}>
                  <div className="eg-catalog-select">
                    <EgSelect
                      placeholder={i18n["catalogues_placeholder"]}
                      options={getOptions(scope.children)}
                      onChange={(sel) => handleKeyChange(sel.value)}
                      value={getOptions().find((item) => item.value == selectedKey)}
                      theme={theme}
                      isValid={!showErrorLastChild || !(Array.isArray(valid) && valid.length > 0 && tryToNext)}
                    />
                  </div>
                </div>
              )}
            </div>
            {isLastChild && (
              <ReturnErrorMessages tryNext={tryToNext} valid={valid} />
            )}
          </EgCol>
        )}
        {scope?.next && selectedKey && !rerenderChild && (
          <WidgetCatalogueInput
            containerName={getContainerName()}
            containerDefault={getContainerDefault()}
            schema={schema}
            scope={scope.next}
            value={value}
            valid={valid}
            origin={origin}
            path={`${path}next.`}
            labelShowed={labelShowed ? true : showLabel}
            pathSchema={pathSchema}
            pathData={pathData}
            nestNum={nestNum + 1}
            handleValidate={handleValidate}
            returnValidClass={returnValidClass}
            tryToNext={tryToNext}
            entireSchema={entireSchema}
            entireFormData={entireFormData}
            setSchemaState={setSchemaState}
            setFormData={setFormData}
            constraints={constraints}
            setConstraints={setConstraints}
            rerenderParent={rerenderParent}
            forEdition={forEdition}
            lang={lang}
            theme={theme}
            formParams={formParams}
          />
        )}
      </>
    );
  }

  // RADIO type catalogue
  if (
    type === INPUT_TYPE_CATALOGUE_RADIO ||
    type === INPUT_TYPE_CATALOGUE_RADIO_DESCRIPTION ||
    type === INPUT_TYPE_CATALOGUE_RADIO_CONDITIONAL ||
    type === INPUT_TYPE_CATALOGUE_RADIO_DESCRIPTION_CONDITIONAL
  ) {
    return (
      <>
        {showExampleModal && (
          <ModalExamples
            itemExamples={itemExamples}
            setShowExampleModal={setShowExampleModal}
            theme={theme}
          />
        )}
        {!middlewareHidden() && !hideInputByContraints && (
          <EgCol
            md={schema.grid || 12}
            className={`eg-preview-input-container ${showErrorLastChild ? returnValidClass("container") : ""}`}
          >
            {(showLabel && !labelShowed && !containerName) && (
              <div className="eg-flex">
                <EgLabel required={schema.required}>
                  {schema.locales?.[lang] === undefined ? schema.label : schema.locales[lang]}
                </EgLabel>
                {hidden && !isForLanding && (
                  <span className="eg-icon-hidden eg-ms-2">👁</span>
                )}
              </div>
            )}
            {containerName ? (
              <EgLabel required={schema.required && !labelShowed}>
                {containerName}
              </EgLabel>
            ) : (
              nestNum !== 0 && <div style={{ height: 32 }}></div>
            )}
            <div className="eg-preview-input-container-inp eg-no-mt-auto">
              <CatalogueRadioInput
                scope={scope}
                pathData={pathData}
                type={type}
                nestNum={nestNum}
                grid={grid}
                returnValidClass={returnValidClass}
                showErrorLastChild={showErrorLastChild}
                GetDescritionAndExamples={GetDescritionAndExamples}
                handleKeyChange={handleKeyChange}
                selectedKey={selectedKey}
                isSelected={isSelected}
                orderChildren={orderChildren}
                theme={theme}
              />
            </div>
            {!isLastChild && grid === 12 && <hr className="eg-my-2" />}
            {isLastChild && (
              <ReturnErrorMessages tryNext={tryToNext} valid={valid} />
            )}
          </EgCol>
        )}
        {scope?.next && selectedKey && !rerenderChild && (
          <WidgetCatalogueInput
            containerName={getContainerName()}
            containerDefault={getContainerDefault()}
            schema={schema}
            scope={scope.next}
            value={value}
            valid={valid}
            origin={origin}
            path={`${path}next.`}
            labelShowed={labelShowed ? true : showLabel}
            pathSchema={pathSchema}
            pathData={pathData}
            nestNum={nestNum + 1}
            handleValidate={handleValidate}
            returnValidClass={returnValidClass}
            tryToNext={tryToNext}
            entireSchema={entireSchema}
            entireFormData={entireFormData}
            setSchemaState={setSchemaState}
            setFormData={setFormData}
            constraints={constraints}
            setConstraints={setConstraints}
            lang={lang}
            theme={theme}
            formParams={formParams}
          />
        )}
      </>
    );
  }

  return null;
};

export default WidgetCatalogueInput;
